// components/default/forms/SearchableSelect.tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { Feather } from "@expo/vector-icons";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Dimensions,
  FlatList,
  Keyboard,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

const { height } = Dimensions.get("window");

interface Option {
  label: string;
  value: string;
}

interface SearchableSelectProps {
  label: string;
  placeholder?: string;
  options?: Option[];
  value?: string;
  onValueChange: (value: string, label: string) => void;
  error?: string;
  containerStyle?: object;
  onSearch?: (query: string) => void;
  searchValue?: string;
  onSearchChange?: (query: string) => void;
  loading?: boolean;
  disabled?: boolean;
  clearable?: boolean;
}

export default function SearchableSelect({
  label,
  placeholder = "Select an option",
  options = [],
  value,
  onValueChange,
  error,
  containerStyle,
  onSearch,
  searchValue: externalSearchValue,
  onSearchChange,
  loading,
  disabled = false,
  clearable = false,
}: SearchableSelectProps) {
  const { colors, spacing, borderRadius } = useAppTheme();
  const [modalVisible, setModalVisible] = useState(false);
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const [selectedOption, setSelectedOption] = useState<Option | undefined>();
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const searchQuery =
    externalSearchValue !== undefined
      ? externalSearchValue
      : internalSearchQuery;
  const isControlled = externalSearchValue !== undefined;

  // Keyboard listeners for dynamic padding
  useEffect(() => {
    const keyboardWillShow = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      (e) => setKeyboardHeight(e.endCoordinates.height),
    );
    const keyboardWillHide = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide",
      () => setKeyboardHeight(0),
    );
    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, []);

  useEffect(() => {
    if (options && value) {
      const found = options.find((opt) => opt.value === value);
      setSelectedOption(found);
    } else {
      setSelectedOption(undefined);
    }
  }, [value, options]);

  const filteredOptions = options
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : [];

  const handleSearchChange = (text: string) => {
    if (onSearchChange) {
      onSearchChange(text);
    } else {
      setInternalSearchQuery(text);
    }
    if (onSearch) {
      onSearch(text);
    }
  };

  const handleSelect = (opt: Option) => {
    setSelectedOption(opt);
    onValueChange(opt.value, opt.label);
    setModalVisible(false);
    if (!isControlled) {
      setInternalSearchQuery("");
    }
  };

  const handleClear = () => {
    setSelectedOption(undefined);
    onValueChange("", "");
    if (!isControlled) {
      setInternalSearchQuery("");
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    if (!isControlled) {
      setInternalSearchQuery("");
    }
  };

  const styles = StyleSheet.create({
    container: {
      marginBottom: spacing.lg,
    },
    label: {
      fontSize: 14,
      fontWeight: "500",
      color: colors.text,
      marginBottom: spacing.xs,
    },
    trigger: {
      borderWidth: 1,
      borderColor: error ? colors.danger : colors.border,
      borderRadius: borderRadius.md,
      padding: spacing.md,
      backgroundColor: colors.background,
    },
    triggerDisabled: {
      opacity: 0.6,
    },
    triggerRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    triggerText: {
      fontSize: 16,
      color: selectedOption ? colors.text : colors.placeholder,
      flex: 1,
    },
    clearIcon: {
      marginLeft: spacing.sm,
    },
    errorText: {
      fontSize: 12,
      color: colors.danger,
      marginTop: spacing.xs,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.5)",
      justifyContent: "flex-end",
    },
    modalContent: {
      backgroundColor: colors.background,
      borderTopLeftRadius: borderRadius.lg,
      borderTopRightRadius: borderRadius.lg,
      maxHeight: height * 0.8,
      paddingTop: spacing.lg,
      paddingBottom: Platform.OS === "ios" ? 20 : 0,
    },
    searchContainer: {
      paddingHorizontal: spacing.lg,
      marginBottom: spacing.md,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
    },
    searchInput: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: borderRadius.md,
      padding: spacing.md,
      fontSize: 16,
      color: colors.text,
      backgroundColor: colors.background,
    },
    loader: {
      marginRight: spacing.sm,
    },
    optionItem: {
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    optionText: {
      fontSize: 16,
      color: colors.text,
    },
    closeButton: {
      padding: spacing.md,
      alignItems: "center",
      borderTopWidth: 1,
      borderTopColor: colors.border,
      marginTop: spacing.sm,
    },
    closeText: {
      fontSize: 16,
      color: colors.primary,
      fontWeight: "500",
    },
  });

  return (
    <View style={[styles.container, containerStyle]}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity
        style={[styles.trigger, disabled && styles.triggerDisabled]}
        onPress={() => !disabled && setModalVisible(true)}
        disabled={disabled}
        activeOpacity={0.7}
      >
        <View style={styles.triggerRow}>
          <Text style={styles.triggerText}>
            {selectedOption?.label || placeholder}
          </Text>
          {clearable && selectedOption && !disabled && (
            <TouchableOpacity
              onPress={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              style={styles.clearIcon}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Feather name="x-circle" size={18} color={colors.textLight} />
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
      {error && <Text style={styles.errorText}>{error}</Text>}

      <Modal
        visible={modalVisible}
        transparent
        animationType="slide"
        onRequestClose={closeModal}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={closeModal}
        >
          <View style={styles.modalContent}>
            <View style={styles.searchContainer}>
              <TextInput
                style={styles.searchInput}
                placeholder="Search..."
                value={searchQuery}
                onChangeText={handleSearchChange}
                autoFocus
                placeholderTextColor={colors.placeholder}
              />
              {loading && (
                <ActivityIndicator
                  size="small"
                  color={colors.primary}
                  style={styles.loader}
                />
              )}
            </View>
            <FlatList
              data={filteredOptions}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.optionItem}
                  onPress={() => handleSelect(item)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.optionText}>{item.label}</Text>
                </TouchableOpacity>
              )}
              showsVerticalScrollIndicator
              keyboardShouldPersistTaps="always"
              maintainVisibleContentPosition={{
                minIndexForVisible: 0,
              }}
              contentContainerStyle={{
                paddingBottom: keyboardHeight + spacing.lg,
              }}
            />
            <TouchableOpacity style={styles.closeButton} onPress={closeModal}>
              <Text style={styles.closeText}>Close</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}
